import { useEmergencyCallData } from '../hooks/useEmergencyCallData'
import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Alert, Flex, Modal, Text } from '@hivewatch/dark'

import { ControlledSelect, FacilityOptionLabel } from '@/components/ui'
import {
  OptionI,
  createFacilityAddressFilter,
} from '@/utils/forms/optionBuilders'
import { getFacilityNoOptionsMessage } from '@/utils/forms/validation'
import { tVar } from '@/utils/i18n'

interface DarkGlobalEmergencyCallModalIProps {
  isOpen: boolean
  onClose: () => void
  emergencyCallCategoryId: string | null
  handleOnClick: (
    facilityId?: string,
    facilityName?: string,
    incidentId?: string,
    incidentDisplayId?: string,
    emergencyNumber?: string
  ) => void
}

interface FormInputsIProps {
  facility: OptionI
}

/**
 * Rebrand re-skin of {@link GlobalEmergencyCallModal} built on `@hivewatch/dark`
 * (ENG-34044): dark `Modal`, dark `Alert` callouts (the legacy Chakra v1
 * `ModalAlert` was unreadable in dark mode), and the dark `Select` via
 * `ControlledSelect`. The call flow — `useEmergencyCallData` — is shared with
 * the legacy modal untouched.
 */
export const DarkGlobalEmergencyCallModal = ({
  isOpen,
  onClose,
  handleOnClick,
  emergencyCallCategoryId,
}: DarkGlobalEmergencyCallModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { showError } = useToasts()
  const form = useForm<FormInputsIProps>({
    mode: 'onChange',
  })
  const { control, watch } = form
  const watchedFacility = watch('facility')

  const {
    facilityOptions,
    isCreatingIncident,
    isLoadingFacilities,
    isFacilitiesError,
    isLoadingFacilityDetails,
    emergencyNumber: hookEmergencyNumber,
    isEmergencyCallingConfigured,
    handleCreateIncidentAndCall,
    setFacilityName,
  } = useEmergencyCallData({
    facility: watchedFacility,
    emergencyCallCategoryId,
    handleOnClick,
    onClose,
  })

  const handlePlaceCallClick = async () => {
    if (!watchedFacility?.value) {
      showError(t('emergency_call:please_select_facility_error'))
      return
    }

    await handleCreateIncidentAndCall(watchedFacility.value)
  }

  const canNotMakeEmergencyCall =
    watchedFacility &&
    !isLoadingFacilityDetails &&
    !isEmergencyCallingConfigured

  const canProceed = Boolean(
    watchedFacility?.value && isEmergencyCallingConfigured
  )

  const hintColor =
    colorScheme === 'light' ? 'light.text.secondary' : 'text.secondary'

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDestructive
      isSaveDisabled={!canProceed || isCreatingIncident}
      isSaveLoading={isCreatingIncident}
      maxW='600px'
      onClose={onClose}
      onSave={handlePlaceCallClick}
      saveButtonLabel={t('emergency_call:place_call')}
      showCancelButton
      size='compact'
      title={t('emergency_call:emergency_call')}
    >
      <Flex direction='column' gap='16px'>
        {!watchedFacility && (
          <Text color={hintColor} textStyle='body'>
            {t('emergency_call:please_select_facility')}
          </Text>
        )}

        {watchedFacility && isEmergencyCallingConfigured && (
          <Alert
            colorScheme={colorScheme}
            description={tVar(t, 'emergency_call:facility_call_label', {
              facility: watchedFacility?.label,
              number: hookEmergencyNumber,
            })}
            title={t('common:warning_title')}
            variant='warning'
          />
        )}

        {canNotMakeEmergencyCall && (
          <Alert
            colorScheme={colorScheme}
            description={`${t(
              'emergency_call:emergency_call_not_configured_for'
            )}${watchedFacility?.label ?? ''}`}
            title={t('common:warning_title')}
            variant='warning'
          />
        )}

        <ControlledSelect
          colorScheme={colorScheme}
          control={control}
          data-testid='globalEmergencyCallModal_facilityName'
          defaultValue={null}
          // Filters by facility name, short name, and address.
          // Without filterOption, React Select defaults to label-only search,
          // which would hide address/short-name-only matches the server
          // already returned.
          filterOption={createFacilityAddressFilter}
          formatOptionLabel={(option, meta) => (
            <FacilityOptionLabel
              colorScheme={colorScheme}
              isValueContext={meta?.context === 'value'}
              option={option}
            />
          )}
          isClearable
          isLoading={isLoadingFacilities}
          isSearchable
          label={t('common:facility')}
          name='facility'
          noOptionsMessage={({ inputValue }) =>
            getFacilityNoOptionsMessage(
              inputValue,
              isFacilitiesError,
              t('common:facility_search_error')
            )
          }
          onInputChange={setFacilityName}
          options={facilityOptions}
          placeholder={t('emergency_call:select_facility_placeholder')}
          rules={{ required: false }}
        />

        {canNotMakeEmergencyCall && (
          <Alert
            colorScheme={colorScheme}
            description={t('emergency_call:contact_hint')}
            title={t('emergency_call:need_help')}
            variant='info'
          />
        )}
      </Flex>
    </Modal>
  )
}
